import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type {
	BOTTOM_OF_LIST,
	TOP_OF_LIST,
	BEFORE_CARD,
	TRANSITION_ZONE,
	BOTTOM_OF_CARD_GROUP,
} from '../../../../model/constants';
import type { Transition } from '../../../../model/transition/types';
import type { CardId } from '../../../../model/types';
import {
	type OptimisticId,
	type OptimisticUiMetaWithDisabledFlag,
	type OptimisticUiMeta,
	type OptimisticUiMetaWithFlag,
	beginOptimisticUiMeta,
	commitOptimisticUiMeta,
	revertOptimisticUiWithFlagMeta,
	revertOptimisticUiWithDisabledFlagMeta,
	revertOptimisticUiMeta,
} from '../../meta/optimistic-ui-meta';

export const CARDS_MOVE = 'state.actions.cards.move.CARDS_MOVE' as const;

export type CardsMovePayload =
	| {
			cardListId: string;
			isBetweenLists: boolean;
			isFromActiveSprint: boolean;
			placement:
				| typeof TOP_OF_LIST
				| typeof BOTTOM_OF_LIST
				| typeof BOTTOM_OF_CARD_GROUP
				| typeof TRANSITION_ZONE;
			cardIds?: CardId[]; // if cards not defined then consume selected ones,
	  }
	| {
			cardListId: string;
			isBetweenLists: boolean;
			isFromActiveSprint: boolean;
			placement: typeof BEFORE_CARD;
			beforeCardId: CardId;
			cardIds?: CardId[];
	  };

export type CardsMoveAction = {
	type: typeof CARDS_MOVE;
	payload: CardsMovePayload;
	meta: {
		analyticsEvent: UIAnalyticsEvent;
	};
};

export const cardsMove = (
	payload: CardsMovePayload,
	analyticsEvent: UIAnalyticsEvent,
): CardsMoveAction => ({
	type: CARDS_MOVE,
	payload,
	meta: {
		analyticsEvent,
	},
});

export const CARDS_MOVE_REQUEST = 'state.actions.cards.move.CARDS_MOVE_REQUEST' as const;

export type CardsMoveRequestPayload = {
	cardIds: CardId[];
	cardListId: string;
	newCardOrder?: CardId[];
	newCardOrderGlobal?: CardId[];
	isBetweenLists: boolean;
	beforeCardId: CardId | null;
	afterCardId: CardId | null;
	selectedTransition?: Transition;
	targetStatusId?: string;
};

export type CardsMoveRequestAction = {
	type: typeof CARDS_MOVE_REQUEST;
	payload: CardsMoveRequestPayload;
	meta: {
		analyticsEvent: UIAnalyticsEvent;
	} & OptimisticUiMeta;
};

export const cardsMoveRequest = (
	payload: CardsMoveRequestPayload,
	analyticsEvent: UIAnalyticsEvent,
): CardsMoveRequestAction => ({
	type: CARDS_MOVE_REQUEST,
	payload,
	meta: {
		analyticsEvent,
		...beginOptimisticUiMeta(),
	},
});

export const CARDS_MOVE_NON_OPTIMISTIC_REQUEST =
	'state.actions.cards.move.CARDS_MOVE_NON_OPTIMISTIC_REQUEST' as const;

export type CardsMoveNonOptimisticRequestAction = {
	type: typeof CARDS_MOVE_NON_OPTIMISTIC_REQUEST;
	payload: CardsMoveRequestPayload;
	meta: {
		optimisticId: OptimisticId;
		analyticsEvent: UIAnalyticsEvent;
	};
};

export const cardsMoveNonOptimisticRequest = (
	optimisticId: OptimisticId,
	payload: CardsMoveRequestPayload,
	analyticsEvent: UIAnalyticsEvent,
): CardsMoveNonOptimisticRequestAction => ({
	type: CARDS_MOVE_NON_OPTIMISTIC_REQUEST,
	payload,
	meta: {
		optimisticId,
		analyticsEvent,
	},
});

export const CARDS_MOVE_SUCCESS = 'state.actions.cards.move.CARDS_MOVE_SUCCESS' as const;

export type CardsMoveSuccessAction = {
	type: typeof CARDS_MOVE_SUCCESS;
	payload: {
		cardIds: CardId[];
		cardListId: string;
		targetStatusId?: string;
	};
	meta: {
		analyticsEvent: UIAnalyticsEvent;
	} & OptimisticUiMeta;
};

export const cardsMoveSuccess = (
	optimisticId: string,
	cardIds: CardId[],
	cardListId: string,
	analyticsEvent: UIAnalyticsEvent,
	targetStatusId?: string,
): CardsMoveSuccessAction => ({
	type: CARDS_MOVE_SUCCESS,
	payload: {
		cardIds,
		cardListId,
		targetStatusId,
	},
	meta: {
		analyticsEvent,
		...commitOptimisticUiMeta(optimisticId),
	},
});

export const CARDS_MOVE_FAILURE = 'state.actions.cards.move.CARDS_MOVE_FAILURE' as const;

export type CardsMoveFailureAction = {
	type: typeof CARDS_MOVE_FAILURE;
	payload: {
		cardIds: CardId[];
		cardListId: string;
		error: Error;
	};
	meta: {
		analyticsEvent: UIAnalyticsEvent;
	} & OptimisticUiMetaWithFlag;
};

export const cardsMoveFailure = (
	optimisticId: string,
	cardIds: CardId[],
	cardListId: string,
	analyticsEvent: UIAnalyticsEvent,
	error: Error,
): CardsMoveFailureAction => ({
	type: CARDS_MOVE_FAILURE,
	payload: {
		cardIds,
		cardListId,
		error,
	},
	meta: {
		analyticsEvent,
		...revertOptimisticUiWithFlagMeta(optimisticId),
	},
});

export const CARDS_TRANSITION_AND_RANK_FAILURE = 'CARDS_TRANSITION_AND_RANK_FAILURE' as const;

export type CardsTransitionAndRankFailureAction = {
	type: typeof CARDS_TRANSITION_AND_RANK_FAILURE;
	payload: {
		errorMessages: string[];
	};
	meta: {
		analyticsEvent: UIAnalyticsEvent;
	} & OptimisticUiMetaWithDisabledFlag;
};

export const cardsTransitionAndRankFailure = (
	optimisticId: string,
	errorMessages: string[],
	analyticsEvent: UIAnalyticsEvent,
): CardsTransitionAndRankFailureAction => ({
	type: CARDS_TRANSITION_AND_RANK_FAILURE,
	payload: {
		errorMessages,
	},
	meta: {
		analyticsEvent,
		...revertOptimisticUiWithDisabledFlagMeta(optimisticId),
	},
});

export const CARDS_MOVE_CANCEL = 'state.actions.cards.move.CARDS_MOVE_CANCEL' as const;

export type CardsMoveCancelAction = {
	type: typeof CARDS_MOVE_CANCEL;
	meta: {
		analyticsEvent: UIAnalyticsEvent;
	} & OptimisticUiMeta;
};

export const cardsMoveCancel = (
	optimisticId: string,
	analyticsEvent: UIAnalyticsEvent,
): CardsMoveCancelAction => ({
	type: CARDS_MOVE_CANCEL,
	meta: {
		analyticsEvent,
		...revertOptimisticUiMeta(optimisticId),
	},
});

export const CARDS_MOVE_SET = 'state.actions.cards.move.CARDS_MOVE_SET' as const;

export type CardsMoveSetPayload = {
	cardIds: CardId[];
	cardListId: string;
	isBetweenLists: boolean;
	beforeCardId: CardId | null;
	afterCardId: CardId | null;
};

export type CardsMoveSetAction = {
	type: typeof CARDS_MOVE_SET;
	payload: CardsMoveSetPayload;
};

export const cardsMoveSet = (payload: CardsMoveSetPayload): CardsMoveSetAction => ({
	type: CARDS_MOVE_SET,
	payload,
});

export const CARDS_MOVE_DISABLED = 'state.actions.cards.move.CARDS_MOVE_DISABLED' as const;

export type CardsMoveDisabledAction = {
	type: typeof CARDS_MOVE_DISABLED;
	payload: {
		boardId: string;
		isAdmin: boolean;
	};
	meta: {
		analyticsEvent: UIAnalyticsEvent;
	} & OptimisticUiMetaWithDisabledFlag;
};

export const cardsMoveDisabled = (
	optimisticId: string,
	analyticsEvent: UIAnalyticsEvent,
	boardId: string,
	isAdmin: boolean,
): CardsMoveDisabledAction => ({
	type: CARDS_MOVE_DISABLED,
	payload: {
		boardId,
		isAdmin,
	},
	meta: {
		analyticsEvent,
		...revertOptimisticUiWithDisabledFlagMeta(optimisticId),
	},
});

export const CARD_DRAG_DISALLOWED = 'state.actions.cards.move.CARD_DRAG_DISALLOWED' as const;

export type CardDragDisallowedAction = {
	type: typeof CARD_DRAG_DISALLOWED;
};

export const cardDragDisallowed = (): CardDragDisallowedAction => ({
	type: CARD_DRAG_DISALLOWED,
});

export type Action =
	| CardsMoveAction
	| CardsMoveSuccessAction
	| CardsMoveFailureAction
	| CardsMoveRequestAction
	| CardsMoveNonOptimisticRequestAction
	| CardsMoveSetAction
	| CardsMoveDisabledAction
	| CardsMoveCancelAction
	| CardsTransitionAndRankFailureAction
	| CardDragDisallowedAction;
